import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"
import { Slot } from "radix-ui"
import { SearchIcon } from "lucide-react"

import { Separator } from "@/components/ui/separator"
import { FramePanel } from "@/components/ui/frame"
import { ScrollArea } from "@/components/ui/scroll-area"
import { ScopeWrapper } from "@/components/ui/scope-wrapper"

function ItemGroup({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      role="list"
      data-slot="item-group"
      className={cn("group/item-group flex flex-col", className)}
      {...props}
    />
  )
}

function ItemSeparator({
  className,
  ...props
}: React.ComponentProps<typeof Separator>) {
  return (
    <Separator
      data-slot="item-separator"
      orientation="horizontal"
      className={cn("my-0", className)}
      {...props}
    />
  )
}

const itemVariants = cva(
  "group/item flex flex-wrap items-center rounded-md text-sm outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
  {
    variants: {
      variant: {
        default: "border border-transparent bg-transparent transition-colors duration-100 [a]:hover:bg-accent/50",
        outline: "border border-border transition-colors duration-100 [a]:hover:bg-accent/50",
        muted: "border border-transparent bg-muted/50 transition-colors duration-100 [a]:hover:bg-accent/50",
        // A row of an ItemList: rounded, never an eased transition (lists
        // switch their hover instantly, links included). A row that is
        // itself the control (asChild onto a label/button/link) also gets the
        // hover and the scope corners — see Item; a row merely holding
        // controls stays still, so it never looks selectable.
        row: "rounded-lg transition-none [label&]:cursor-pointer [button&]:cursor-pointer",
      },
      size: {
        default: "gap-4 p-4",
        sm: "gap-2.5 px-4 py-3",
      },
    },
    compoundVariants: [
      { variant: "row", size: "default", class: "gap-3 p-2" },
      { variant: "row", size: "sm", class: "gap-2.5 px-2.5 py-1.5" },
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Item({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"div"> &
  VariantProps<typeof itemVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : "div"
  const clickableRow = variant === "row" && asChild
  const item = (
    <Comp
      data-slot="item"
      data-variant={variant}
      data-size={size}
      className={cn(itemVariants({ variant, size }), clickableRow && "hover:bg-muted", className)}
      {...props}
    />
  )
  return clickableRow ? <ScopeWrapper className="block">{item}</ScopeWrapper> : item
}

/**
 * A list of `Item variant="row"` (or SelectList) rows: a flat panel, padded,
 * rows a hair apart. Give it a max height (`className="max-h-72"`) and it
 * scrolls with a fade at the cut edge. Pass `search` for a ghost search
 * field at the top of the panel (focused on mount) that stays put while
 * the rows scroll under it — the filtering is yours.
 *
 * @example
 * import { Badge } from "@/components/ui/badge"
 * import { Item, ItemContent, ItemDescription, ItemList, ItemTitle } from "@/components/ui/item"
 *
 * type Payment = { id: string; label: string; date: string; amount: string; refunded: boolean }
 *
 * export function Payments({ payments }: { payments: Payment[] }) {
 *   return (
 *     <ItemList className="max-h-72">
 *       {payments.map((p) => (
 *         <Item key={p.id} variant="row" size="sm">
 *           <ItemContent>
 *             <ItemTitle>{p.label}</ItemTitle>
 *             <ItemDescription>{p.date}</ItemDescription>
 *           </ItemContent>
 *           <Badge variant={p.refunded ? "warning" : "success"}>{p.amount}</Badge>
 *         </Item>
 *       ))}
 *     </ItemList>
 *   )
 * }
 */
/** ItemList's search field: controlled, the caller filters its own rows. */
interface ItemListSearch {
  value: string
  onValueChange: (value: string) => void
  placeholder?: string
}

function ItemListSearchField({ value, onValueChange, placeholder = "Search" }: ItemListSearch) {
  const ref = React.useRef<HTMLInputElement>(null)
  // After the frame: a dialog's own open auto-focus (Radix, on mount) would
  // otherwise take the focus back from a plain autoFocus.
  React.useEffect(() => {
    const id = requestAnimationFrame(() => ref.current?.focus({ preventScroll: true }))
    return () => cancelAnimationFrame(id)
  }, [])
  return (
    <div data-slot="item-list-search" className="flex items-center gap-2 border-b px-4">
      <SearchIcon className="size-4 shrink-0 text-muted-foreground" />
      <input
        ref={ref}
        type="search"
        value={value}
        onChange={(e) => onValueChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        className="h-10 w-full min-w-0 bg-transparent text-sm outline-none placeholder:text-muted-foreground [&::-webkit-search-cancel-button]:hidden"
      />
    </div>
  )
}

function ItemList({
  className,
  children,
  search,
  ...props
}: React.ComponentProps<"div"> & { search?: ItemListSearch }) {
  return (
    <FramePanel className="p-0">
      {search && <ItemListSearchField {...search} />}
      <ScrollArea scrollFade className={cn("flex flex-col rounded-xl", className)}>
        <div role="list" data-slot="item-list" className="flex flex-col gap-0.5 p-2" {...props}>
          {children}
        </div>
      </ScrollArea>
    </FramePanel>
  )
}

const itemMediaVariants = cva(
  "flex shrink-0 items-center justify-center gap-2 group-has-[[data-slot=item-description]]/item:translate-y-0.5 group-has-[[data-slot=item-description]]/item:self-start [&_svg]:pointer-events-none",
  {
    variants: {
      variant: {
        default: "bg-transparent",
        icon: "size-9 rounded-md border bg-background [&_svg:not([class*='size-'])]:size-4",
        image:
          "size-10 overflow-hidden rounded-md [&_img]:size-full [&_img]:object-cover",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

function ItemMedia({
  className,
  variant = "default",
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof itemMediaVariants>) {
  return (
    <div
      data-slot="item-media"
      data-variant={variant}
      className={cn(itemMediaVariants({ variant, className }))}
      {...props}
    />
  )
}

function ItemContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="item-content"
      className={cn(
        "flex flex-1 flex-col gap-1 [&+[data-slot=item-content]]:flex-none",
        className
      )}
      {...props}
    />
  )
}

function ItemTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="item-title"
      className={cn(
        "flex w-fit items-center gap-2 text-sm leading-snug font-medium",
        className
      )}
      {...props}
    />
  )
}

function ItemDescription({ className, ...props }: React.ComponentProps<"p">) {
  return (
    <p
      data-slot="item-description"
      className={cn(
        "line-clamp-2 text-sm leading-normal font-normal text-balance text-muted-foreground group-data-[variant=row]/item:text-xs",
        "[&>a]:underline [&>a]:underline-offset-4 [&>a:hover]:text-primary",
        className
      )}
      {...props}
    />
  )
}

function ItemActions({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="item-actions"
      className={cn("flex items-center gap-2", className)}
      {...props}
    />
  )
}

function ItemHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="item-header"
      className={cn(
        "flex basis-full items-center justify-between gap-2",
        className
      )}
      {...props}
    />
  )
}

function ItemFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="item-footer"
      className={cn(
        "flex basis-full items-center justify-between gap-2",
        className
      )}
      {...props}
    />
  )
}

export {
  itemVariants,
  Item,
  ItemList,
  ItemMedia,
  ItemContent,
  ItemActions,
  ItemGroup,
  ItemSeparator,
  ItemTitle,
  ItemDescription,
  ItemHeader,
  ItemFooter,
}
export type { ItemListSearch }
